import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import SlideToggle from 'react-slide-toggle';
import classNames from 'classnames';

import { safeContent } from 'utils';
import { AccordionContext } from 'contexts';
import Radio from '../Radio';
import { Image } from '../Image';

const Card = ({
  eventKey,
  title,
  children,
  collapsedView,
  adClass,
  titleClasses,
  bodyClasses,
  headerIcon,
  type,
  disabled,
  onSetActiveKey,
  id,
}) => {
  const { activeKey, toggleEvents, setActiveKey } = useContext(
    AccordionContext,
  );
  const expanded = activeKey === eventKey;
  const collapsed = !expanded;

  const handleOnClick = (_e) => {
    if (disabled) return;

    if (eventKey === activeKey && type === 'payment-method') return;

    if (expanded) {
      setActiveKey(null);
      onSetActiveKey(null);
      return;
    }

    setActiveKey(eventKey);
    onSetActiveKey(eventKey);
  };

  switch (type) {
    case 'default':
      return (
        <SlideToggle
          collapsed={collapsed}
          toggleEvent={toggleEvents[eventKey] || 0}
        >
          {({ setCollapsibleElement, toggleState }) => (
            <div
              className={classNames(
                'card',
                expanded ? 'expanded' : '',
                adClass,
              )}
            >
              <div
                className="card-header"
                onClick={handleOnClick}
                id={id}
              >
                <h2
                  className={classNames('card-title', titleClasses)}
                >
                  <span
                    disabled={disabled}
                    className={classNames(
                      'toggle-button',
                      toggleState.toLowerCase(),
                    )}
                    dangerouslySetInnerHTML={safeContent(title)}
                    style={{ height: 'auto' }}
                  ></span>
                </h2>
              </div>
              {collapsed && collapsedView && (
                <div className={classNames('card-body', bodyClasses)}>
                  {collapsedView}
                </div>
              )}
              <div ref={setCollapsibleElement}>
                <div className={classNames('card-body', bodyClasses)}>
                  {children}
                </div>
              </div>
            </div>
          )}
        </SlideToggle>
      );

    case 'payment-method':
      return (
        <SlideToggle
          collapsed={collapsed}
          toggleEvent={toggleEvents[eventKey] || 0}
        >
          {({ setCollapsibleElement, toggleState }) => (
            <div
              className={classNames(
                'tw-flex tw-flex-col tw-border-b tw-py-4',
                adClass,
              )}
            >
              <div
                className="tw-flex tw-items-center tw-justify-between tw-w-full"
                onClick={handleOnClick}
              >
                <div className="tw-flex items-center">
                  <Radio
                    checked={expanded}
                    className="tw-flex tw-items-center"
                    inputClassName="tw-relative tw-mt-0"
                  />
                  <div
                    className={classNames(
                      'tw-flex tw-items-center tw-gap-4 tw-cursor-pointer',
                      expanded && 'tw-cursor-default',
                    )}
                  >
                    {headerIcon && (
                      <Image
                        src={`/assets/images/payments/${headerIcon.src}.svg`}
                        alt={`Accept ${title}`}
                        layout="intrinsic"
                        width={headerIcon.width}
                        height={headerIcon.height}
                      />
                    )}
                    <span
                      disabled={disabled}
                      className={classNames(
                        'toggle-button tw-font-semibold',
                        toggleState.toLowerCase(),
                      )}
                      dangerouslySetInnerHTML={safeContent(title)}
                      style={{ height: 'auto' }}
                    ></span>
                  </div>
                </div>
              </div>
              {collapsed && collapsedView && (
                <div className={classNames(bodyClasses)}>
                  {collapsedView}
                </div>
              )}
              <div ref={setCollapsibleElement}>
                <div className={classNames(bodyClasses)}>
                  {children}
                </div>
              </div>
            </div>
          )}
        </SlideToggle>
      );

    default:
      return (
        <SlideToggle
          collapsed={collapsed}
          toggleEvent={toggleEvents[eventKey] || 0}
        >
          {({ setCollapsibleElement, toggleState }) => (
            <div className={`acc-item ${adClass}`}>
              <h5>
                <span
                  disabled={disabled}
                  className={`toggle-button ${toggleState.toLowerCase()}`}
                  dangerouslySetInnerHTML={safeContent(title)}
                  onClick={handleOnClick}
                  style={{ height: 'auto' }}
                ></span>
              </h5>
              {collapsed && collapsedView && (
                <div className="card-body">{collapsedView}</div>
              )}
              <div ref={setCollapsibleElement}>
                <div className="collapse-wrap">{children}</div>
              </div>
            </div>
          )}
        </SlideToggle>
      );
  }
};

Card.propTypes = {
  eventKey: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  collapsedView: PropTypes.node,
  headerIcon: PropTypes.object,
  adClass: PropTypes.string,
  titleClasses: PropTypes.string,
  bodyClasses: PropTypes.string,
  type: PropTypes.oneOf(['default']),
  disabled: PropTypes.bool,
  onSetActiveKey: PropTypes.func,
  id: PropTypes.string,
};

Card.defaultProps = {
  collapsedView: null,
  type: 'default',
  disabled: false,
  titleClasses: '',
  bodyClasses: '',
  headerIcon: null,
  onSetActiveKey: (key) => {},
  id: undefined,
};

export default React.memo(Card);
